import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "../../lib/api-client";
import type { components } from "../../lib/api-types.gen";

/* Contract-derived types (ADR-S1-005): the Betriebslog ([F-015], [F-013]). */
export type OperationsLogType = components["schemas"]["OperationsLogType"];
export type OperationsLogListItem = components["schemas"]["OperationsLogListItem"];
export type OperationsLogListResponse = components["schemas"]["OperationsLogListResponse"];
export type OperationsLogEntry = components["schemas"]["OperationsLogEntryResponse"];
export type OperationsLogLinkedIncident = components["schemas"]["OperationsLogLinkedIncident"];
export type IncidentOperationsLogResponse = components["schemas"]["IncidentOperationsLogResponse"];
export type CreateOperationsLogEntryRequest = components["schemas"]["CreateOperationsLogEntryRequest"];

export interface OperationsLogFilters {
  type?: OperationsLogType;
  areaId?: string;
  /** Substring of the Kurzbeschreibung. */
  q?: string;
  limit?: number;
}

export const operationsLogKeys = {
  all: ["operations-log"] as const,
  list: (filters: OperationsLogFilters) => ["operations-log", "list", filters] as const,
  entry: (id: string) => ["operations-log", "entry", id] as const,
  ofIncident: (incidentId: string) => ["operations-log", "incident", incidentId] as const,
};

/** Query string from the filters, empty values omitted. */
export function operationsLogQuery(filters: OperationsLogFilters, cursor?: string): string {
  const sp = new URLSearchParams();
  const params: Record<string, string | number | undefined> = {
    type: filters.type,
    areaId: filters.areaId,
    q: filters.q,
    limit: filters.limit,
    cursor,
  };
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== "") sp.set(key, String(value));
  }
  const qs = sp.toString();
  return qs ? `?${qs}` : "";
}

/** GET /operations-log - newest start first; "Mehr laden" walks nextCursor. */
export function useOperationsLog(filters: OperationsLogFilters = {}) {
  return useInfiniteQuery({
    queryKey: operationsLogKeys.list(filters),
    queryFn: ({ pageParam }) =>
      apiFetch<OperationsLogListResponse>(`/operations-log${operationsLogQuery(filters, pageParam)}`),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last: OperationsLogListResponse) => last.nextCursor ?? undefined,
  });
}

/** GET /operations-log/{id} - one entry with its incidents. */
export function useOperationsLogEntry(id: string) {
  return useQuery({
    queryKey: operationsLogKeys.entry(id),
    queryFn: () => apiFetch<OperationsLogEntry>(`/operations-log/${id}`),
    enabled: id.length > 0,
  });
}

/** GET /incidents/{id}/operations-log - the entries linked to an incident. */
export function useIncidentOperationsLog(incidentId: string) {
  return useQuery({
    queryKey: operationsLogKeys.ofIncident(incidentId),
    queryFn: () => apiFetch<IncidentOperationsLogResponse>(`/incidents/${incidentId}/operations-log`),
    enabled: incidentId.length > 0,
  });
}

/** POST /operations-log (`oplog.write`). */
export function useCreateOperationsLogEntry() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateOperationsLogEntryRequest) =>
      apiFetch<OperationsLogEntry>("/operations-log", {
        method: "POST",
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: operationsLogKeys.all });
    },
  });
}

/** POST /incidents/{id}/operations-log (`oplog.link`). */
export function useLinkOperationsLog(incidentId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (operationsLogId: string) =>
      apiFetch<OperationsLogListItem>(`/incidents/${incidentId}/operations-log`, {
        method: "POST",
        body: JSON.stringify({ operationsLogId }),
      }),
    onSuccess: () => {
      // Both ends of the link change ([F-013]).
      void queryClient.invalidateQueries({ queryKey: operationsLogKeys.all });
    },
  });
}
